import { bpToPct, fmtExpiry, formatMoney } from "./money";

// Device-agnostic receipt model. Phase 3 ESC/POS printing can consume the same structure.
export function buildReceiptModel(sale, settings) {
  const dp = sale.decimals;
  const m = (n) => formatMoney(n, dp);
  const snap = sale.business_snapshot || {};
  const t = sale.totals;
  const items = sale.lines.map((ln) => ({
    name: ln.name,
    qtyLine: `${ln.qty} x ${m(ln.unit_minor)}`,
    amount: m(ln.gross),
    discount: ln.line_discount ? `-${m(ln.line_discount)}` : null,
    batches: (ln.allocations || []).filter((a) => a.batch_no).map((a) => `B# ${a.batch_no}  EXP ${fmtExpiry(a.expiry)}  x${a.qty}`),
  }));
  const totals = [{ label: "Subtotal", value: m(t.gross_total) }];
  if (t.line_discount_total) totals.push({ label: "Item discounts", value: `-${m(t.line_discount_total)}` });
  if (t.cart_discount) totals.push({ label: "Cart discount", value: `-${m(t.cart_discount)}` });
  t.tax_breakdown.forEach((tb) => {
    totals.push({
      label: `${sale.tax_label} ${bpToPct(tb.rate_bp)}%${sale.tax_inclusive ? " (incl.)" : ""}`,
      value: m(tb.amount),
    });
  });
  totals.push({ label: `TOTAL ${sale.currency}`, value: m(t.total), strong: true });
  const pay = sale.payment;
  const payment = pay.credit_note ? [{ label: `Credit note ${pay.credit_note.number}`, value: m(pay.credit_note.amount) }] : [];
  payment.push({ label: "Paid by", value: pay.method === "cash" ? "CASH" : "CARD" });
  if (pay.credit_note && pay.method === "card") payment.push({ label: "Card amount", value: m(pay.amount) });
  if (pay.method === "cash") {
    payment.push({ label: "Tendered", value: m(pay.tendered) });
    payment.push({ label: "Change", value: m(pay.change) });
  }
  const d = new Date(sale.created_at);
  return {
    width: settings?.receipt_width || "80mm",
    businessName: snap.name,
    header: (snap.receipt_header && snap.receipt_header !== snap.name ? snap.receipt_header : "").split("\n").filter(Boolean),
    branch: snap.branch_name,
    branchAddress: snap.branch_address || null,
    branchPhone: snap.branch_phone ? `Tel: ${snap.branch_phone}` : null,
    taxNumber: snap.tax_number ? `${sale.tax_label} No: ${snap.tax_number}` : null,
    receiptCode: sale.receipt_code,
    codeLabel: "Receipt",
    barcode: sale.receipt_code,
    notes: [],
    date: d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
    time: d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    items,
    totals,
    payment,
    footer: (snap.receipt_footer || "").split("\n").filter(Boolean),
  };
}

const docHead = (doc, settings) => {
  const snap = doc.business_snapshot || {};
  const d = new Date(doc.created_at || doc.issued_at);
  return {
    width: settings?.receipt_width || "80mm",
    businessName: snap.name,
    header: [],
    branch: snap.branch_name,
    branchAddress: snap.branch_address || null,
    branchPhone: snap.branch_phone ? `Tel: ${snap.branch_phone}` : null,
    taxNumber: null,
    date: d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
    time: d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    footer: [],
  };
};
const dmy = (iso) => new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

export function buildReturnModel(ret, settings) {
  const m = (n) => formatMoney(n, ret.decimals);
  const cn = ret.credit_note;
  return {
    ...docHead(ret, settings),
    title: "RETURN",
    codeLabel: "Return",
    receiptCode: ret.number,
    barcode: null,
    notes: [`Original receipt ${ret.receipt_code}`, ...(ret.reason ? [`Reason: ${ret.reason}`] : [])],
    items: ret.lines.map((l) => ({
      name: l.name,
      qtyLine: `${l.qty} x ${l.condition === "resellable" ? "Resellable" : "Damaged"}`,
      amount: m(l.refund_minor),
      discount: null,
      batches: [`${ret.tax_label} reversed ${m(l.tax_minor)}`, ...(l.allocations || []).filter((a) => a.batch_no).map((a) => `B# ${a.batch_no}`)],
    })),
    totals: [{ label: `${ret.tax_label} reversed`, value: m(ret.tax_total) }, { label: `REFUND ${ret.currency}`, value: m(ret.refund_total), strong: true }],
    payment: ret.method === "cash" ? [{ label: "Cash refunded", value: m(ret.refund_total) }] : [{ label: "Credit note", value: cn?.number || "" }],
  };
}

export function buildCreditNoteModel(cn, settings) {
  const m = (n) => formatMoney(n, cn.decimals);
  return {
    ...docHead(cn, settings),
    title: "CREDIT NOTE",
    codeLabel: "Credit note",
    receiptCode: cn.number,
    barcode: cn.number,
    notes: [`Issued ${dmy(cn.issued_at)}`, `Expires ${dmy(cn.expires_on)}`, `Original receipt ${cn.receipt_code}`, `Valid at ${cn.branch_name} only · Use in full in one purchase`],
    items: [],
    totals: [{ label: `AMOUNT ${cn.currency}`, value: m(cn.amount), strong: true }],
    payment: [],
  };
}
